import clsx from 'clsx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

import { ChartWell, WellsGroup } from '../../../presets/drilling-wells-chart/entities';
import { WellsViewSettingsProvider } from '../../../presets/drilling-wells-chart/wells-view-settings-provider';
import { useMovableContainer } from '../../../shared/use-movable-container';
import { Viewport } from '../../../shared/viewport/viewport';
import { ComparingWellsData } from '../../comparing-wells-chart/data';

import { ComparingWellsDataHeader } from './comparing-wells-data-header';

import styles from './data-headers.module.scss';

interface Props {
  data: ComparingWellsData.ViewItem[];
  viewport: Viewport;
  isCompactView: boolean;
  viewProvider: WellsViewSettingsProvider;
  onIsCollapseToggle(item: WellsGroup): void;
  containerWidth?: number;
  containerHeight?: number;
  className?: string;
  onScroll?(offset: number): void;
  onHeightChange?(height: number): void;
}

export const ComparingWellsDataHeaders = observer(function ComparingWellsDataHeaders({
  data,
  viewport,
  isCompactView,
  viewProvider,
  containerWidth,
  containerHeight,
  className,
  onScroll,
  onHeightChange,
  onIsCollapseToggle,
}: Props) {
  const { containerRef, handleScroll } = useMovableContainer({ onScroll });

  useEffect(() => {
    const container = containerRef.current;

    if (container) {
      const resizeObserver = new ResizeObserver(() => {
        if (container) {
          const { height } = container.getBoundingClientRect();
          onHeightChange?.(height);
        }
      });

      resizeObserver.observe(container);

      return () => {
        resizeObserver.unobserve(container);
      };
    }
  }, [containerHeight, containerWidth, onHeightChange, containerRef]);

  return (
    <div className={clsx(className, styles.wrapper)} ref={containerRef} onWheel={handleScroll}>
      <div className={styles.headers}>
        {data.map((item) => {
          if (item instanceof ChartWell || item instanceof WellsGroup) {
            return (
              <ComparingWellsDataHeader
                key={item.getKey()}
                item={item}
                viewport={viewport}
                viewProvider={viewProvider}
                isCompactView={isCompactView}
                onIsCollapseToggle={onIsCollapseToggle}
              />
            );
          }

          return null;
        })}
      </div>
    </div>
  );
});
